'use client';

import {
  Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import { useSWRConfig } from 'swr';
import { TailwindSpinner } from '@components/common';
import { useSwrJobListCardsInfinite } from '@hooks/useSwrApi/endpoints/job/useSwrJobListCardsInfinite';
import { JobCardList } from '@components/templates/job/Card/JobCardList';
import { buildSearchParams } from '@utils/data-fetching/searchParams';
import { cn } from '@utils/cn';
import { useFiltersQueryParamsAppFolder } from '@hooks/filters/useFiltersQueryParamsAppFolder';
import { JobSearchFilters } from '@components/features/filters/JobSearchFilters';
import { useMissionControlBestieChat } from '@components/contexts/MissionControlBestieChatContext';
import { JobSearchQueryParams } from '@type/filters';
import { useSetHeaderSearchContext } from '@components/contexts/HeaderSearchContext';
import { CandidateJobSearchForm } from '@components/features/candidate-job-search';
import { useJobSearchFiltersFromSidebar } from './useJobSearchFiltersFromSidebar';
import { CandidateFloatingNavBar, type CandidateNavButton } from './CandidateFloatingNavBar';
import { MissionControlBestieChat } from './MissionControlBestieChat';
import { MissionControlBestieContent } from './MissionControlBestieContent';
import { MyBestieLanding } from '../../templates/my-bestie-landing';
import { useSidebarNavigation } from '../../contexts/SidebarNavigationContext';

interface JobListContentProps {
  locale: Locale;
  isAuthenticated?: boolean;
  /** SSR-fetched job data so SWR uses it as fallback and the backend receives auth headers on first load. */
  serverSideJobData?: ApiSchema<'CursorJobResult'>;
  /** Set to false to suppress the CandidateFloatingNavBar on this page. Defaults to true. */
  showCandidateFloatingNavBar?: boolean;
  /** Override which nav bar button appears selected (has border, non-clickable). Auto-set to 'coaching' on the bestie/new-search view. */
  navBarSelectedButton?: CandidateNavButton;
}

/**
 * @description Displays job list based on current sidebar selection.
 * - "New Job Search" → shows all/recommended jobs (no filters)
 * - Selected alert → shows jobs filtered by alert's saved search criteria
 */
export const JobListContent = (props: JobListContentProps) => {
  const {
    locale, isAuthenticated = false, serverSideJobData,
    showCandidateFloatingNavBar = true, navBarSelectedButton,
  } = props;
  const { t } = useTranslation('common');
  const { isChatOpen, promptRequest, closeChat } = useMissionControlBestieChat();

  // Get filters from sidebar navigation
  const {
    filters, isNewSearch, isMyBestieLanding, isJobSearchForm,
  } = useJobSearchFiltersFromSidebar();
  const { currentView, navigateToCandidateSearch } = useSidebarNavigation();

  // When editing, the current view carries the pre-fill filters directly
  const editSearchFilters = typeof currentView === 'object' && 'editSearch' in currentView
    ? currentView.editSearch
    : undefined;

  // Filter drawer state — managed independently so the floating filter button can open it
  const { queryParams, setQueryParams } = useFiltersQueryParamsAppFolder<'cursor'>({
    paginationType: 'cursor',
    preserveNumericLastSegment: true,
  });

  // Keys managed by SidebarNavigationContext — must be excluded from headerQueryParams
  // to prevent the header's useFiltersHub from feeding them back into setQueryParams,
  // which would cause useAppHistoryAndURL to push a stale URL and trigger an infinite loop.
  const headerQueryParams = useMemo(() => {
    const sidebarKeys = ['keyword', 'location', 'sort', 'careerLevels', 'domain', 'employmentType', 'spokenLanguages', 'remote'];
    const strip = (obj: Record<string, unknown>) => Object.fromEntries(
      Object.entries(obj).filter(([key]) => !sidebarKeys.includes(key)),
    );
    // Strip sidebar-managed keys from BOTH sources:
    // - filters: these come from the sidebar selection and shouldn't flow to the header
    // - queryParams: the header's useFiltersHub can inject e.g. keyword='' back into
    //   queryParams, which would leak into headerQueryParams and sustain the feedback loop
    return { ...strip((filters ?? {}) as Record<string, unknown>), ...strip(queryParams as Record<string, unknown>) };
  }, [filters, queryParams]);
  // Pass a no-op setQueryParams to the header context. On MC pages, keyword is managed
  // by SidebarNavigationContext. Passing the real setQueryParams allows the header's
  // useFiltersHub to inject keyword='' back into queryParams, creating an infinite
  // re-render loop (useFiltersHub always returns a new object reference, which cascades
  // through the context back into setQueryParams).
  const headerSetQueryParamsNoop = useCallback(() => {}, []);
  useSetHeaderSearchContext({ queryParams: headerQueryParams, setQueryParams: headerSetQueryParamsNoop });

  // Merge sidebar-derived filters (alert/keyword) with drawer-applied URL params.
  // `filters` carries keyword/alert fields; `queryParams` carries domain, careerLevels, etc.
  // `queryParams` wins on overlap so explicit drawer selections always take effect,
  // except for keyword which is managed by the sidebar — strip it from queryParams
  // to prevent a stale keyword='' (injected by the header's useFiltersHub) from
  // overriding the sidebar's keyword.
  const urlParams = useMemo(() => {
    const { keyword: sidebarKeyword, ...drawerParams } = queryParams as Record<string, unknown>;
    void sidebarKeyword;
    const combined = { ...(filters ?? {}), ...drawerParams };
    return buildSearchParams(combined, '&', false);
  }, [filters, queryParams]);

  // Pagination state
  const [, setCurrentPage] = useState<string>('');

  useEffect(() => {
    setCurrentPage(''); // Reset to first page when search changes
  }, [urlParams]);

  // Only fetch jobs when the list view is actually visible — avoids an unfiltered
  // background fetch while on the landing/form views, which would flash as stale
  // data (keepPreviousData) the first time a user picks a search.
  const shouldFetchJobs = !isNewSearch && !isMyBestieLanding && !isJobSearchForm;

  // Fetch job cards with infinite scroll
  const swrResponse = useSwrJobListCardsInfinite({
    locale,
    limit: 24,
    urlParams,
    initialCursor: '',
    serverSideData: serverSideJobData,
    shouldFetch: shouldFetchJobs,
  });

  const {
    data: jobListData,
    size,
    isLoading,
  } = swrResponse;

  // When the backend returns a valid searchId, a new alert was created — refresh the sidebar
  const { mutate: globalMutate } = useSWRConfig();
  const lastRefreshedSearchId = useRef<number | null>(null);
  useEffect(() => {
    const firstGroup = jobListData?.length ? jobListData[0] : undefined;
    const rawSearchId = firstGroup?.searchId;
    if (rawSearchId == null) return;
    const parsedSearchId = parseInt(rawSearchId, 10);
    if (Number.isInteger(parsedSearchId) && parsedSearchId > 0 && parsedSearchId !== lastRefreshedSearchId.current) {
      lastRefreshedSearchId.current = parsedSearchId;
      // Revalidate without clearing the cache (passing undefined would cause a flash to empty data
      // which breaks the alertId → filters lookup and creates an infinite refetch loop).
      void globalMutate(
        (key: unknown) => Array.isArray(key) && typeof key[0] === 'string' && key[0].includes('/v1/user/job-search-alerts'),
      );
    }
  }, [jobListData, globalMutate]);

  const mainContent = (() => {
    if (isMyBestieLanding) {
      return <MyBestieLanding locale={locale} isAuthenticated={isAuthenticated} />;
    }
    if (isJobSearchForm) {
      return (
        <CandidateJobSearchForm
          key={JSON.stringify(editSearchFilters)}
          onSearch={navigateToCandidateSearch}
          initialFilters={editSearchFilters}
        />
      );
    }
    if (isNewSearch) {
      return <MissionControlBestieContent locale={locale} isAuthenticated={isAuthenticated} />;
    }
    return (
      <>
        {isChatOpen ? (
          <MissionControlBestieChat
            locale={locale}
            isAuthenticated={isAuthenticated}
            selectedButton={navBarSelectedButton}
            promptRequest={promptRequest}
            onClose={closeChat}
          />
        ) : (
          <>
            <div className="container mx-auto px-4 py-6">
              {/* Loading state - initial load */}
              {isLoading && size === 1 && (
                <div className="flex items-center justify-center py-16">
                  <TailwindSpinner className="size-8 text-primary" />
                </div>
              )}

              {/* Job cards list */}
              {!isLoading || size > 1 ? (
                <JobCardList
                  {...swrResponse}
                  t={t}
                  setCurrentPage={setCurrentPage}
                  locale={locale}
                  paginationType="cursor"
                />
              ) : null}
            </div>

          </>
        )}
      </>
    );
  })();

  return (
    <div className={cn('flex min-h-full flex-col bg-surface-100', !isChatOpen && !isMyBestieLanding && !isNewSearch && 'pb-20')}>
      {mainContent}

      {/* Floating navigation bar — shown on all candidate views unless opted out. Excluded on isNewSearch (bestie/coaching) since that view renders its own inline nav bar. */}
      {showCandidateFloatingNavBar && !isChatOpen && !isMyBestieLanding && !isNewSearch && (
        <CandidateFloatingNavBar
          locale={locale}
          selectedButton={navBarSelectedButton}
          pillBackground="surface-100"
          isAuthenticated={isAuthenticated}
        />
      )}

      {/* Filter drawer — opened by the filter button in the floating nav bar */}
      <JobSearchFilters
        queryParams={queryParams as JobSearchQueryParams}
        setQueryParams={setQueryParams as Dispatch<SetStateAction<JobSearchQueryParams>>}
      />
    </div>
  );
};
